import { request } from "../../utils/request";
import { VIDEO_CDN_PREFIX } from "../../config/video";

/**
 * 将 page/get 返回的 cloneAudioUrl 转为可播放的完整 URL（相对路径会拼 CDN 前缀）
 */
export const resolvePublicVoicePreviewFullUrl = (
  item: VoicePublic.VoiceToneItem,
  cdnPrefix: string = VIDEO_CDN_PREFIX
): string | null => {
  const raw = item.cloneAudioUrl?.trim();
  if (!raw) return null;
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  const normalizedPath = raw.startsWith("/") ? raw : `/${raw}`;
  return `${cdnPrefix}${normalizedPath}`;
};

/**
 * 将 page/get 返回的 audioUrl（用户上传的原始音频）转为可播放的完整 URL
 * 相对路径会拼 CDN/TOS 前缀，用于 API 文档页音色试听
 */
export const resolvePublicVoiceAudioFullUrl = (
  item: VoicePublic.VoiceToneItem,
  cdnPrefix: string = VIDEO_CDN_PREFIX
): string | null => {
  const raw = item.audioUrl?.trim();
  if (!raw) return null;
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  const normalizedPath = raw.startsWith("/") ? raw : `/${raw}`;
  return `${cdnPrefix}${normalizedPath}`;
};

/**
 * 公共音色详情
 * 接口：GET /video/public-voice-tone/get
 * 响应 data：含 audioUrl（上传原音频）、cloneAudioUrl（合成试听）、cloneText 等
 * @param id 公共音色ID（publicVoiceToneId）
 */
export const getPublicVoiceToneByIdApi = (id: number): Promise<{ code: number; data: VoicePublic.VoiceToneItem; msg?: string }> => {
  return request<{ code: number; data: VoicePublic.VoiceToneItem; msg?: string }>(
    "/video/public-voice-tone/get",
    {
      method: "GET",
      params: { id }
    }
  );
};

/**
 * 公共音色库分页列表
 * 接口：GET /video/public-voice-tone/page
 * 实际请求路径：/video/public-voice-tone/page（网关前缀由环境配置）
 * 列表项含 audioUrl（上传原音频）、cloneAudioUrl（合成试听）等
 */
export const getPublicVoiceTonePageApi = (
  params: VoicePublic.VoiceTonePageParams
) => {
  return request<VoicePublic.VoiceTonePageResponse>(
    "/video/public-voice-tone/page",
    {
      method: "GET",
      params,
    }
  );
};

/**
 * 新增公共音色
 * 接口：POST /video/public-voice-tone/add
 * 请求：form-data，字段 voiceName、langCode、genderValue、timbreValue、ageValue、cloneText、published、audioFile（文件）
 * 响应：{ code: 0, data: 新增记录 id, msg: "" }
 */
export const addPublicVoiceToneApi = (
  params: VoicePublic.AddVoiceToneParams
): Promise<VoicePublic.AddVoiceToneResponse> => {
  const formData = new FormData();
  formData.append("audioFile", params.audioFile);
  const queryParams: Record<string, string | boolean> = {
    voiceName: params.voiceName,
    langCode: params.langCode,
    genderValue: params.genderValue,
    timbreValue: params.timbreValue,
    ageValue: params.ageValue,
    cloneText: params.cloneText,
    published: params.published,
  };
  if (params.description !== undefined) {
    queryParams.description = params.description;
  }

  return request<VoicePublic.AddVoiceToneResponse>(
    "/video/public-voice-tone/add",
    {
      method: "POST",
      params: queryParams,
      body: formData,
    }
  );
};

/**
 * 编辑公共音色
 * 接口：PUT /video/public-voice-tone/update
 * 请求：form-data，字段 id、voiceName、langCode、genderValue、timbreValue、ageValue、published、cloneText（可选）、audioFile（可选，文件）
 * 响应：{ code: 0, data: true, msg: "" }
 */
export const updatePublicVoiceToneApi = (
  params: VoicePublic.UpdateVoiceToneParams
): Promise<VoicePublic.UpdateVoiceToneResponse> => {
  const formData = new FormData();
  formData.append("id", String(params.id));
  formData.append("voiceName", params.voiceName);
  formData.append("langCode", params.langCode);
  formData.append("genderValue", params.genderValue);
  formData.append("timbreValue", params.timbreValue);
  formData.append("ageValue", params.ageValue);
  formData.append("published", String(params.published));
  if (params.cloneText !== undefined) {
    formData.append("cloneText", params.cloneText);
  }
  if (params.description !== undefined) {
    formData.append("description", params.description);
  }
  if (params.audioFile) {
    formData.append("audioFile", params.audioFile);
  }

  return request<VoicePublic.UpdateVoiceToneResponse>(
    "/video/public-voice-tone/update",
    {
      method: "PUT",
      body: formData,
    }
  );
};

/**
 * 删除公共音色
 * 接口：POST /video/public-voice-tone/delete
 * 请求：params，字段 id
 * 响应：{ code: 0, data: true, msg: "" }
 */
export const deletePublicVoiceToneApi = (
  params: VoicePublic.DeleteVoiceToneParams
): Promise<VoicePublic.DeleteVoiceToneResponse> => {
  return request<VoicePublic.DeleteVoiceToneResponse>(
    "/video/public-voice-tone/delete",
    {
      method: "DELETE",
      params,
    }
  );
};

